// Pure rules over stored assets for thumbnails (ADR-022): which image types a
// browser can paint, and which asset stands for a whole job on a list row.
// A content hash as the portal and Nexus both write it: lowercase hex sha256.
// Checked before any hash is put into a storage key.
export function isSha256Hex(value) {
    return /^[0-9a-f]{64}$/.test(value);
}
// Which image types a browser will actually PAINT in an <img>. Narrower than
// `content_type.startsWith('image/')` on purpose: HEIC/HEIF are images a photo
// slot accepts BECAUSE a phone writes them by default, and no browser decodes
// them, and neither does any browser decode TIFF or PSD, so the broad test paints
// a broken-image icon instead of a picture.
//
// ONE predicate, read by the mid-upload preview, the persisted tile, the photo
// strip and the artwork mockup. Two would let a file look better while it is
// uploading than it does once it lands, which reads as the upload having broken.
//
// SVG stays: an SVG drawn by <img> runs no script, and the SPA only ever loads it
// from a blob URL. The preview proxy serves it as an attachment under a sandbox
// CSP, so it is never a same-origin document.
export const RENDERABLE_IMAGE_TYPES = new Set([
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'image/bmp',
    'image/avif',
    'image/svg+xml',
]);
export function isRenderableImage(contentType) {
    return typeof contentType === 'string' && RENDERABLE_IMAGE_TYPES.has(contentType);
}
// The roles a list row may show as the job's picture, in order. Any other
// uploaded role comes after these, earliest first.
export const HERO_ROLE_ORDER = ['cover', 'front', 'general', 'combined'];
// Roles that are not a picture of the job: data lists, a manuscript's interior
// pages, and the finishing masks (die line, foil, emboss) that read as a black
// shape on white.
export const NON_HERO_ROLES = new Set([
    'variable_data',
    'variable_data_list',
    'die_cut',
    'foil',
    'emboss',
    'guts',
]);
// The asset that stands for a job on a list row. A pure function of stored
// assets (R26): never of render progress or the clock, so the same quote shows
// the same hero on every read. Only a live asset with a content hash qualifies,
// since a rendition is keyed by the hash. Null means the caller shows the
// family glyph.
export function pickHeroAsset(assets) {
    const candidates = assets.filter((a) => a.status !== 'deleted' &&
        a.content_hash !== null &&
        !NON_HERO_ROLES.has(a.component_role));
    if (candidates.length === 0)
        return null;
    const rank = (role) => {
        const i = HERO_ROLE_ORDER.indexOf(role);
        return i === -1 ? HERO_ROLE_ORDER.length : i;
    };
    const time = (v) => (v instanceof Date ? v.getTime() : Date.parse(v));
    const sorted = [...candidates].sort((a, b) => rank(a.component_role) - rank(b.component_role) ||
        time(a.created_at) - time(b.created_at) ||
        a.id.localeCompare(b.id));
    return sorted[0] ?? null;
}
//# sourceMappingURL=rendition.js.map